import { z } from "zod";

export const domainRegex =
	/^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

export const propertyDomainSchema = z.object({
	domain: z
		.string()
		.min(1, "Domain is required")
		.regex(domainRegex, "Please enter a valid domain name"),
});

export type PropertyDomainFormValues = z.infer<typeof propertyDomainSchema>;

/**
 * Subject types the Add Property dialog can create.
 *
 * PRODUCT uses a seed URL (a representative page) instead of a root domain,
 * since a product is usually a section of a parent brand's site.
 *
 * CATEGORY tracks a *market* rather than a company, and has no focal brand:
 * there is nothing of the customer's to crawl, so it takes no domain and no
 * seed URL. Its identity key is the category name (required); an optional
 * free-text description sharpens the market characterisation and is stored as
 * the first entry of the snapshot's `notes` (there is no column for it).
 */
export const addPropertySubjectTypeValues = [
	"BRAND",
	"PERSON",
	"PRODUCT",
	"CATEGORY",
] as const;
export type AddPropertySubjectType =
	(typeof addPropertySubjectTypeValues)[number];

/** Loose URL check for the PRODUCT seed-URL input. Accepts http/https URLs. */
export const urlLooksValid = (s: string) => {
	try {
		const u = new URL(s);
		return u.protocol === "http:" || u.protocol === "https:";
	} catch {
		return false;
	}
};

/**
 * Form schema for the multi-subjectType Add Property dialog.
 * - BRAND requires a valid domain.
 * - PERSON requires a name.
 * - PRODUCT requires a valid `seedUrl` (the page we'll crawl); `name` is
 *   optional and shown as a friendly display name.
 * - CATEGORY requires a `name` (the market's name, which becomes the property
 *   name); `description` is optional free text about who is buying and what
 *   they are choosing between.
 */
export const addPropertyFormSchema = z
	.object({
		subjectType: z.enum(addPropertySubjectTypeValues).default("BRAND"),
		domain: z.string().optional().default(""),
		name: z.string().optional().default(""),
		entityRef: z.string().optional().default(""),
		seedUrl: z.string().optional().default(""),
		description: z.string().max(2000).optional().default(""),
	})
	.superRefine((data, ctx) => {
		if (data.subjectType === "BRAND") {
			if (!data.domain || data.domain.length === 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Domain is required",
					path: ["domain"],
				});
			} else if (!domainRegex.test(data.domain)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Please enter a valid domain name",
					path: ["domain"],
				});
			}
		} else if (data.subjectType === "PERSON") {
			if (!data.name || data.name.length === 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Name is required",
					path: ["name"],
				});
			}
		} else if (data.subjectType === "PRODUCT") {
			if (!data.seedUrl || data.seedUrl.length === 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Product page URL is required",
					path: ["seedUrl"],
				});
			} else if (!urlLooksValid(data.seedUrl)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Please enter a valid http(s) URL",
					path: ["seedUrl"],
				});
			}
		} else if (data.subjectType === "CATEGORY") {
			if (!data.name || data.name.length === 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Category name is required",
					path: ["name"],
				});
			}
		}
	});

export type AddPropertyFormValues = z.infer<typeof addPropertyFormSchema>;
